import { ResponseItem, ResponseValueTypes } from './SurveyResponse';
import moment from 'moment';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import surveyUrls from './surveyUrls';
import ExtensionCollection from './ExtensionCollection';
import { SurveyEnablementAnswertypes } from './SurveyItem';
import { EnablementComparators } from './QuestionnaireMapper/QuestionnaireItemUpdater';

export type SurveyCustomComparators = '!=' | '<' | '<=' | '>' | '>=';
export type SurveyComparisonCustomComparators = '<' | '<=' | '>' | '>=';

/**
 * Is type supported by the comparsion (=, !=) comparison types
 */
export const doesAnswerTypeSupportEquality = (
  answerType: SurveyEnablementAnswertypes,
): boolean => {
  // Types that support equality comparsion (=, !=) comparison types
  const supportedEqualityComparisonType: SurveyEnablementAnswertypes[] = [
    'answerBoolean',
    'answerDecimal',
    'answerInteger',
    'answerDate',
    'answerDateTime',
    'answerTime',
    'answerString',
    'answerUri',
    'answerCoding',
    'answerQuantity',
  ];

  return supportedEqualityComparisonType.includes(answerType);
};

/**
 * Is type supported by the comparsion (<, >, <=, >=) comparison types
 */
export const doesAnswerTypeSupportComparison = (
  answerType: SurveyEnablementAnswertypes,
): boolean => {
  // Types that support the comparsion (<, >, <=, >=) comparison types
  const supportedComparisonComparatorTypes: SurveyEnablementAnswertypes[] = [
    'answerDate',
    'answerDateTime',
    'answerDecimal',
    'answerInteger',
    'answerQuantity',
    'answerTime',
  ];

  return supportedComparisonComparatorTypes.includes(answerType);
};

/**
 * Is type supported by the comparsion (hasAnswer, doesNotHaveAnswer) comparison types
 */
export const doesTypeAnswerSupportExistence = (
  answerType: SurveyEnablementAnswertypes,
): boolean => {
  // Types that support answer existence (hasAnswer, doesNotHaveAnswer) comparison types
  const supportedAnswerExistenceTypes: SurveyEnablementAnswertypes[] = [
    'answerDecimal',
    'answerInteger',
    'answerDate',
    'answerDateTime',
    'answerTime',
    'answerString',
    'answerUri',
    'answerAttachment',
    'answerCoding',
    'answerQuantity',
  ];

  return supportedAnswerExistenceTypes.includes(answerType);
};

/**
 * Does the given comparison not need an associated value configured
 */
export const doesComparatorNotNeedValue = (
  comparator: EnablementComparators,
): boolean => {
  // Comparators that do not need a value for the rule
  const comparatorsWithoutValues: EnablementComparators[] = [
    'hasAnswer',
    'doesNotHaveAnswer',
  ];

  return comparatorsWithoutValues.includes(comparator);
};

const runComparison = (
  responseValue: number,
  configuredCompValue: number,
  comparison: SurveyComparisonCustomComparators,
): boolean => {
  // default to true on invalid input
  if (
    !comparison ||
    isNil(configuredCompValue) ||
    !isNumber(configuredCompValue)
  ) {
    return true;
  }

  if (isNil(responseValue) || !isNumber(responseValue)) return false;

  switch (comparison) {
    case '<':
      return responseValue < configuredCompValue;
    case '<=':
      return responseValue <= configuredCompValue;
    case '>':
      return responseValue > configuredCompValue;
    case '>=':
      return responseValue >= configuredCompValue;
    default:
      console.error(
        `Comparison given unknown equality operator: "${comparison}".  Defaulting to passing equality comparison`,
      );
      return true;
  }
};

const typeCheck = (
  ruleType: ResponseValueTypes,
  responseItem: ResponseItem,
) => {
  return ruleType === (responseItem && responseItem.valueType);
};

interface AnswerType {
  equals: (responseItem: ResponseItem, value: any) => boolean;
  passesComparisonCheck: (responseItem: ResponseItem, value: any) => boolean;
}

export class AnswerBooleanType implements AnswerType {
  _value: any;

  constructor(fhirAnswerValue: any) {
    this._value = fhirAnswerValue;
  }

  equals(responseItem: ResponseItem, value: any): boolean {
    return (
      value === this._value &&
      isBoolean(value) &&
      typeCheck('valueBoolean', responseItem)
    );
  }

  passesComparisonCheck(_responeItem: ResponseItem): boolean {
    return true;
  }
}

export class AnswerNumberType implements AnswerType {
  _value: any;
  _fhirType: ResponseValueTypes;
  _comparisonOperator: SurveyComparisonCustomComparators;

  constructor(
    fhirType: ResponseValueTypes,
    fhirAnswerValue: any,
    comparisonOperator?: SurveyComparisonCustomComparators,
  ) {
    this._value = fhirAnswerValue;
    this._fhirType = fhirType;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._comparisonOperator = comparisonOperator;
  }

  equals(responseItem: ResponseItem, value: any): boolean {
    return (
      value === this._value &&
      isNumber(value) &&
      typeCheck(this._fhirType, responseItem)
    );
  }

  passesComparisonCheck(_responeItem: ResponseItem, value: any): boolean {
    return runComparison(value, this._value, this._comparisonOperator);
  }
}

export class AnswerDateTimeType implements AnswerType {
  private readonly _fhirTimeFormat = 'hh:mm:ss';
  _value: any;
  _fhirType: ResponseValueTypes;
  _comparisonOperator: SurveyComparisonCustomComparators;

  constructor(
    fhirType: ResponseValueTypes,
    fhirAnswerValue: any,
    comparisonOperator?: SurveyComparisonCustomComparators,
  ) {
    this._value = fhirAnswerValue;
    this._fhirType = fhirType;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._comparisonOperator = comparisonOperator;
  }

  _getNumericValue(value: any) {
    return this._fhirType === 'valueTime'
      ? moment(value, this._fhirTimeFormat).valueOf()
      : Date.parse(value);
  }

  equals(responseItem: ResponseItem, value: any): boolean {
    if (!typeCheck(this._fhirType, responseItem) || !this._value || !value) {
      return false;
    }

    const enableValue = this._getNumericValue(this._value);
    const responseValue = this._getNumericValue(value);
    return enableValue === responseValue && !isNaN(enableValue);
  }

  passesComparisonCheck(responseItem: ResponseItem, value: any): boolean {
    if (!responseItem || !typeCheck(this._fhirType, responseItem)) return false;

    const comparisonValue = this._getNumericValue(this._value);
    const responseValue = this._getNumericValue(value);

    return runComparison(
      responseValue,
      comparisonValue,
      this._comparisonOperator,
    );
  }
}

export class AnswerStringType implements AnswerType {
  _value: any;
  _fhirType: ResponseValueTypes;

  constructor(fhirType: ResponseValueTypes, fhirAnswerValue: any) {
    this._value = fhirAnswerValue;
    this._fhirType = fhirType;
  }

  equals(responseItem: ResponseItem, value: any): boolean {
    if (!responseItem) return false;

    if (!typeCheck(this._fhirType, responseItem) || !this._value || !value) {
      return false;
    }

    return this._value === value;
  }

  passesComparisonCheck(_responseItem: ResponseItem, _value: any): boolean {
    return true;
  }
}

export class AnswerAttachmentType implements AnswerType {
  _value: any;

  constructor(fhirAnswerValue: any) {
    this._value = fhirAnswerValue;
  }

  equals(_responseItem: ResponseItem, _value: any): boolean {
    return true;
  }

  passesComparisonCheck(_responseItem: ResponseItem, _value: any): boolean {
    return true;
  }
}

export class AnswerQuantityType implements AnswerType {
  _value: any;
  _comparisonOperator: SurveyComparisonCustomComparators;

  constructor(
    fhirAnswerValue: any,
    comparisonOperator?: SurveyComparisonCustomComparators,
  ) {
    this._value = fhirAnswerValue;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._comparisonOperator = comparisonOperator;
  }

  equals(responseItem: ResponseItem, value: any): boolean {
    if (!responseItem) return false;

    if (
      !typeCheck('valueQuantity', responseItem) ||
      isNil(this._value) ||
      isNil(value)
    ) {
      return false;
    }

    const enablementValue = this._value.value;
    const enablementUnit = this._value.unit;

    const responseValue = value.value;
    const responseUnit = value.unit;

    const valueEqual = enablementValue === responseValue;
    if (!valueEqual) return false;

    // if one of the comparison quantities doesn't have a unit, don't execute comparison
    if (isNil(enablementUnit) || isNil(responseUnit)) return true;

    return enablementUnit === responseUnit;
  }

  passesComparisonCheck(responseItem: ResponseItem, value: any): boolean {
    if (!this._value) return true;

    if (!responseItem || !typeCheck('valueQuantity', responseItem) || !value) {
      return false;
    }

    const responseValue = value.value;
    const comparisonValue = this._value.value;

    return runComparison(
      responseValue,
      comparisonValue,
      this._comparisonOperator,
    );
  }
}

export class AnswerCodingType implements AnswerType {
  _value: any;

  constructor(fhirAnswerValue: any) {
    this._value = fhirAnswerValue;
  }

  equals(responseItem: ResponseItem, value: any): boolean {
    if (!responseItem) return false;

    if (!typeCheck('valueCoding', responseItem) || !this._value || !value) {
      return false;
    }

    const enablementSystem = this._value.system;
    const enablementCode = this._value.code;

    const responseSystem = value.system;
    const responseCode = value.code;

    // only compare systems if present on both values
    const systemComparisonPass =
      !isNil(enablementSystem) && !isNil(responseSystem)
        ? enablementSystem === responseSystem
        : true;

    return (
      enablementCode === responseCode &&
      !isNil(enablementCode) &&
      systemComparisonPass
    );
  }

  passesComparisonCheck(_responseItem: ResponseItem, _value: any): boolean {
    return true;
  }
}

interface Enablement {
  isEnabled: (responeItem: ResponseItem) => boolean;
}

/**
 * Enablement logic based of another survey items response value
 *
 * @export
 * @class AnswerEnablement
 */
export class AnswerEnablement implements Enablement {
  _answerType: AnswerType;
  _customComparator: string;

  constructor(
    fhirEnableWhen: fhir.QuestionnaireItemEnableWhen = {} as fhir.QuestionnaireItemEnableWhen,
  ) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._answerType = null;
    const targetModifierExtension = (
      fhirEnableWhen.modifierExtension || []
    ).find(
      (extension: any = {}) => extension.url === surveyUrls.customComparator,
    );
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._customComparator = targetModifierExtension
      ? targetModifierExtension.valueString
      : null;
    const comparison = this
      ._customComparator as SurveyComparisonCustomComparators;

    if (!isNil(fhirEnableWhen.answerBoolean)) {
      this._answerType = new AnswerBooleanType(fhirEnableWhen.answerBoolean);
    } else if (!isNil(fhirEnableWhen.answerDecimal)) {
      this._answerType = new AnswerNumberType(
        'valueDecimal',
        fhirEnableWhen.answerDecimal,
        comparison,
      );
    } else if (!isNil(fhirEnableWhen.answerInteger)) {
      this._answerType = new AnswerNumberType(
        'valueInteger',
        fhirEnableWhen.answerInteger,
        comparison,
      );
    } else if (!isNil(fhirEnableWhen.answerDate)) {
      this._answerType = new AnswerDateTimeType(
        'valueDate',
        fhirEnableWhen.answerDate,
        comparison,
      );
    } else if (!isNil(fhirEnableWhen.answerDateTime)) {
      this._answerType = new AnswerDateTimeType(
        'valueDateTime',
        fhirEnableWhen.answerDateTime,
        comparison,
      );
    } else if (!isNil(fhirEnableWhen.answerTime)) {
      this._answerType = new AnswerDateTimeType(
        'valueTime',
        fhirEnableWhen.answerTime,
        comparison,
      );
    } else if (!isNil(fhirEnableWhen.answerString)) {
      this._answerType = new AnswerStringType(
        'valueString',
        fhirEnableWhen.answerString,
      );
    } else if (!isNil(fhirEnableWhen.answerUri)) {
      this._answerType = new AnswerStringType(
        'valueUri',
        fhirEnableWhen.answerUri,
      );
    } else if (!isNil(fhirEnableWhen.answerCoding)) {
      this._answerType = new AnswerCodingType(fhirEnableWhen.answerCoding);
    } else if (!isNil(fhirEnableWhen.answerQuantity)) {
      this._answerType = new AnswerQuantityType(
        fhirEnableWhen.answerQuantity,
        comparison,
      );
    } else if (!isNil(fhirEnableWhen.answerAttachment)) {
      this._answerType = new AnswerAttachmentType(
        fhirEnableWhen.answerAttachment,
      );
    } else {
      console.error(
        'Answer-based enablement is not supported for the given answer type',
      );
    }
  }

  get isNotEquals() {
    return this._customComparator === '!=';
  }

  get isComparison() {
    return ['>', '>=', '<', '<='].includes(this._customComparator);
  }

  isEnabled(responseItem: ResponseItem): boolean {
    if (this._answerType === null) return false;

    const safeValue = responseItem && responseItem.value;
    const normalizedValue: any[] = responseItem.isArrayValue
      ? safeValue
      : [safeValue];
    if (this.isComparison) {
      return normalizedValue.some(value =>
        this._answerType.passesComparisonCheck(responseItem, value),
      );
    } else {
      const equalityCount = normalizedValue.filter(value =>
        this._answerType.equals(responseItem, value),
      ).length;
      if (this.isNotEquals) {
        return equalityCount === 0;
      } else {
        return equalityCount > 0;
      }
    }
  }
}

/**
 * Enablement logic based on the existence of another survey response
 *
 * @export
 * @class AnswerEnablement
 */
export class ResponseExistenceEnablement implements Enablement {
  _enableIfResponseExists: boolean;

  constructor(fhirEnableWhen: any = {}) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._enableIfResponseExists = null;

    if (!isBoolean(fhirEnableWhen.hasAnswer)) {
      console.error(
        'Existence-based enablement requires that hasAnswer has a boolean value',
      );
    } else {
      this._enableIfResponseExists = fhirEnableWhen.hasAnswer;
    }
  }

  isEnabled(responseItem: ResponseItem): boolean {
    if (this._enableIfResponseExists === null) return false;

    return (
      (this._enableIfResponseExists && responseItem.hasResponse) ||
      (!this._enableIfResponseExists && !responseItem.hasResponse)
    );
  }
}

export default class ItemEnablement {
  _surveyItemLinkId: string;
  _enableRuleHash: { [id: string]: Enablement[] };
  _fhirEnableWhen: fhir.QuestionnaireItemEnableWhen[];
  _dependentItemLinks: string[];
  _orEnablementRules: boolean;

  constructor(fhirQuestionnaireItem: fhir.QuestionnaireItem) {
    this._surveyItemLinkId = fhirQuestionnaireItem.linkId;
    this._fhirEnableWhen =
      get<
        fhir.QuestionnaireItem,
        'enableWhen',
        fhir.QuestionnaireItemEnableWhen[]
      >(fhirQuestionnaireItem, 'enableWhen', []) || [];

    // create hashmap for enablement field link -> enablement logic
    this._enableRuleHash = {};
    this._fhirEnableWhen
      .filter(Boolean)
      .forEach(
        (
          fhirEnableWhenRule: fhir.QuestionnaireItemEnableWhen = {} as fhir.QuestionnaireItemEnableWhen,
        ) => {
          // question is required
          if (!fhirEnableWhenRule.question) return;

          const existingQuestionRules =
            this._enableRuleHash[fhirEnableWhenRule.question] || [];
          if (
            !isNil(fhirEnableWhenRule.hasAnswer) &&
            isBoolean(fhirEnableWhenRule.hasAnswer)
          ) {
            this._enableRuleHash[fhirEnableWhenRule.question] = [
              ...existingQuestionRules,
              new ResponseExistenceEnablement(fhirEnableWhenRule),
            ];
          } else {
            this._enableRuleHash[fhirEnableWhenRule.question] = [
              ...existingQuestionRules,
              new AnswerEnablement(fhirEnableWhenRule),
            ];
          }
        },
      );

    this._dependentItemLinks = Object.keys(this._enableRuleHash);

    const extensionCollection = ExtensionCollection.initFromBackboneElement(
      fhirQuestionnaireItem,
    );
    this._orEnablementRules = extensionCollection.hasExtension(
      surveyUrls.orEnablementRules,
    );
  }

  /**
   * Does this item have any enablement logic associated with it
   *
   * @readonly
   * @type {Boolean}
   * @memberof ItemEnablement
   */
  get hasEnablementCriteria(): boolean {
    return this._dependentItemLinks.length > 0;
  }

  /**
   * The dependent field links
   *
   * @readonly
   * @type {String[]}
   * @memberof ItemEnablement
   */
  get dependentItemLinks(): string[] {
    return this._dependentItemLinks;
  }

  /**
   * Returns a copy of the original fhir enablewhen rules without the specified link rules in the set
   */
  copyFhirEnableWhenWithoutLinkRules(
    linkIdToOmit: string,
  ): fhir.QuestionnaireItemEnableWhen[] {
    if (
      !linkIdToOmit ||
      !this._fhirEnableWhen ||
      !this.hasEnablementCriteria ||
      !isArray(this._fhirEnableWhen)
    ) {
      return [];
    }

    return this._fhirEnableWhen
      .filter(enablementRule => !!enablementRule && !!enablementRule.question)
      .filter(enablementRule => enablementRule.question !== linkIdToOmit);
  }

  /**
   * Returns a copy of the original fhir enablewhen rules without the rules with the specified link
   * that don't contain a value within the set of values.
   */
  copyFhirEnableWhenWithoutNonExistentValues(
    targetLinkId: string,
    validValueSet: any[] = [],
  ): fhir.QuestionnaireItemEnableWhen[] {
    if (!targetLinkId) return this._fhirEnableWhen;

    const filterCheck = (
      enablementRule: fhir.QuestionnaireItemEnableWhen = {} as fhir.QuestionnaireItemEnableWhen,
    ) => {
      // only validating values against the specified target link
      if (enablementRule.question !== targetLinkId) return true;

      const enablementValue = [
        enablementRule.answerBoolean,
        enablementRule.answerDecimal,
        enablementRule.answerInteger,
        enablementRule.answerDate,
        enablementRule.answerDateTime,
        enablementRule.answerTime,
        enablementRule.answerString,
        enablementRule.answerUri,
        enablementRule.answerAttachment,
        enablementRule.answerCoding,
        enablementRule.answerQuantity,
        enablementRule.answerAttachment,
      ].find((value: any) => !isNil(value));

      if (isNil(enablementValue)) {
        // this could simply be a response existence rule and not a value-based-check rule
        return true;
      }

      // check if value exists in set
      return !!validValueSet.find((value: any) =>
        isEqual(value, enablementValue),
      );
    };

    return this._fhirEnableWhen.filter(filterCheck);
  }

  get originalEnablementRules() {
    return this._fhirEnableWhen;
  }

  /**
   * Given the dependent response values, determines if this field should be enabled
   *
   * @param {ResponseItem[]} [dependentResponseItems=[]]
   * @memberof ItemEnablement
   */
  isEnabled(dependentResponseItems: ResponseItem[] = []): boolean {
    const checkItemRules = (responseItem: ResponseItem) => {
      const targetRules = this._enableRuleHash[responseItem.link] || null;

      if (!targetRules === null) {
        console.error(
          'ItemEnablment given a response item dependency without an associated rule',
        );
        return false;
      }

      const nonNilRules = targetRules.filter(targetRule => !isNil(targetRule));
      const singleRuleCheck = (rule: Enablement) =>
        rule.isEnabled(responseItem);

      return this._orEnablementRules
        ? nonNilRules.some(singleRuleCheck) // OR rules
        : nonNilRules.every(singleRuleCheck); // AND rules
    };

    return this._orEnablementRules
      ? dependentResponseItems.some(checkItemRules) // OR rules
      : dependentResponseItems.every(checkItemRules); // AND rules
  }
}
